import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import DynamicForm from "../../../../data/Axios/DynamicForm";
import { HRService, userManagementUrl } from "../../../../config";
import { allUsers } from "../../../../data/Queries/HRmanagement/leavemanagement";
import { generateAndExecuteMutation, graphqlQuery } from "../../../../data/Axios/DynamicService";

const base_url = HRService.uri;

const ApproversForm = (props) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialValues = {
    userId: props.data ? props.data.userId : "",
  };

  useEffect(() => {
    async function fetchUsers() {
      try {
        setLoading(true);
        console.log("Fetching users from:", userManagementUrl.uri);
        console.log("Query:", allUsers);
        
        const data = await graphqlQuery(allUsers, userManagementUrl.uri);
        
        console.log("Fetched users data:", data);
        
        if (data && Array.isArray(data)) {
          setUsers(data);
        } else {
          console.warn("Unexpected data format:", data);
          setUsers([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        console.error("Error response:", error.response?.data);
        
        Swal.fire({
          icon: "error",
          title: "Error Loading Users",
          text: error.response?.data?.errors?.[0]?.message || "Could not load user list.",
        });
        setUsers([]);
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, []);

  const steps = useMemo(() => {
    return [
      {
        stepLabel: "Approver Selection",
        stepDescription: "Select user(s) to assign as approvers",
        icon: "supervisor_account",
        fields: ["userId"],
      },
    ];
  }, []);

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.firstName} ${user.lastName} (${user.username})`,
  }));

  const fields = [
    {
      name: "userId",
      label: "Approvers",
      type: "select",
      options: userOptions,
      gridColumn: "span 2",
      required: true,
    },
  ];

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Structure 1: Try direct arguments first
      const mutationData = {
        userId: values.userId,
        isActive: 1,
      };

      console.log("=== MUTATION DEBUG ===");
      console.log("Mutation name: addRemoveLeaveApprover");
      console.log("Mutation data:", mutationData);
      console.log("Base URL:", base_url);
      console.log("======================");

      const response = await generateAndExecuteMutation(
        "addRemoveLeaveApprover",
        mutationData,
        'approvers', // Try without input object wrapper
        base_url
      );

      console.log("Mutation response:", response);

      if (response && response.addRemoveLeaveApprover) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Approver added successfully.",
        });
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (error) {
      console.error("=== MUTATION ERROR ===");
      console.error("Error:", error);
      console.error("Error message:", error.message);
      console.error("Error response:", error.response?.data);
      console.error("======================");

      // If the error suggests the mutation structure is wrong, try alternative structure
      if (error.message?.includes("does not exist") || error.message?.includes("field")) {
        console.log("Trying alternative mutation structure with 'approver' wrapper...");
        
        try {
          const alternativeData = {
            UserId: values.userId,
            IsActive: true,
          };
          
          const altResponse = await generateAndExecuteMutation(
            "addRemoveLeaveApprover",
            alternativeData,
            "approver", // Try with wrapper
            base_url
          );
          
          if (altResponse && altResponse.addRemoveLeaveApprover) {
            props.onClose();
            props.onAction();
            Swal.fire({
              icon: "success",
              title: "Success!",
              text: "Approver added successfully.",
            });
            setSubmitting(false);
            return;
          }
        } catch (altError) {
          console.error("Alternative structure also failed:", altError);
        }
      }

      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.message || "Failed to add approver. Please check console for details.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <CircularProgress />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No users available. Please ensure users are configured in the system.</p>
      </div>
    );
  }

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      steps={steps}
      onSubmit={handleSubmit}
    />
  );
};

export default ApproversForm;